/**
 * Event Delegation System for CSP Compliance
 * Centralizes ALL event handlers — click, input, change
 * Removes the need for any inline on* attributes
 */

/**
 * @param {Object} actions - Map of action names to functions
 */
export function setupEventDelegation(actions) {
  // --- Click delegation ---
  document.addEventListener('click', (e) => {
    let target = e.target;
    let depth = 0;
    while (target && depth < 4) {
      const actionName = target.getAttribute && target.getAttribute('data-action');
      // ⚠ PROC-003 — AN UNREGISTERED NAME USED TO FALL THROUGH TO AN ANCESTOR'S ACTION.
      //
      // The gate was `if (actionName && actions[actionName])`. A name that is present but not in
      // the map failed it, so the walk CONTINUED up to four ancestors — and the click could fire
      // a DIFFERENT action belonging to a parent. The register called this "swallows silently";
      // silence was the better half of the behaviour.
      //
      // An element that declares its own `data-action` has claimed the click. If that name is not
      // registered, that is a wiring bug: say so, and stop, rather than doing something else.
      // (All 244 static names are registered today, so this path is a MINE, not a leak —
      // audit 2026-08-16.)
      if (actionName && !actions[actionName]) {
        console.error(`[ZAGX] Unregistered action "${actionName}" — click ignored (not passed to an ancestor)`);
        return;
      }
      if (actionName && actions[actionName]) {
        if (target.tagName === 'A') e.preventDefault();
        let args = [];
        const argsAttr = target.getAttribute('data-args');
        if (argsAttr) {
          try { args = JSON.parse(argsAttr); } catch (_err) {
            console.error(`[ZAGX] Failed to parse data-args for ${actionName}:`, argsAttr);
          }
        }
        try { actions[actionName](...args); } catch (err) {
          console.error(`[ZAGX] Error executing action ${actionName}:`, err);
        }
        return;
      }
      target = target.parentElement;
      depth++;
    }
  });

  // --- Keyboard activation for non-button controls that declare role="button" ---
  // A div with data-action is reachable by Tab (tabindex) but the click handler above never hears
  // Enter or Space. Only elements that promise button behaviour get it; real buttons already do.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const el = e.target;
    if (!el || !el.getAttribute || el.getAttribute('role') !== 'button' || !el.getAttribute('data-action')) return;
    if (/^(BUTTON|A|INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
    e.preventDefault();
    el.click();
  });

  // --- Input delegation (oninput → data-action-input) ---
  document.addEventListener('input', (e) => {
    const actionName = e.target.getAttribute && e.target.getAttribute('data-action-input');
    if (actionName && !actions[actionName]) { console.error(`[ZAGX] Unregistered input action "${actionName}"`); return; }
    if (actionName && actions[actionName]) {
      try { actions[actionName](e); } catch (err) {
        console.error(`[ZAGX] Error in input action ${actionName}:`, err);
      }
    }
  });

  // --- Change delegation (onchange → data-action-change) ---
  document.addEventListener('change', (e) => {
    const actionName = e.target.getAttribute && e.target.getAttribute('data-action-change');
    if (actionName && !actions[actionName]) { console.error(`[ZAGX] Unregistered change action "${actionName}"`); return; }
    if (actionName && actions[actionName]) {
      try { actions[actionName](e); } catch (err) {
        console.error(`[ZAGX] Error in change action ${actionName}:`, err);
      }
    }
  });
}
